import React, { useEffect, useState } from "react";
import {
    fetchPlatformModules,
    enablePlatformModule,
    disablePlatformModule,
    deletePlatformModule,
    createPlatformModule,
    updatePlatformModule,
} from "../../api/platformModuleService";

const PlatformModuleManagerPage = () => {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const emptyForm = {
        moduleKey: "",
        name: "",
        description: "",
        category: "",
        technology: "",
        version: "1.0",
        status: "Active",
        pluginType: "Core",
        assemblyName: "",
        entryPoint: "",
        configurationJson: "",
        dependencies: "",
        executionOrder: 0,
        supportsHotReload: false,
        isEnabled: true,
        isCoreModule: false,
        isResearchModule: false,
        isExperimental: false,
    };
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleEdit = (module) => {
        setEditingId(module.id);
        setForm(module);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editingId) {
            await updatePlatformModule(editingId, form);
        } else {
            await createPlatformModule(form);
        }

        setForm(emptyForm);
        setEditingId(null);
        await loadModules();
    };
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const loadModules = async () => {
        setLoading(true);
        const data = await fetchPlatformModules();
        setModules(data);
        setLoading(false);
    };

    useEffect(() => {
        loadModules();
    }, []);

    const handleEnable = async (id) => {
        await enablePlatformModule(id);
        await loadModules();
    };

    const handleDisable = async (id) => {
        await disablePlatformModule(id);
        await loadModules();
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this module?")) return;
        await deletePlatformModule(id);
        await loadModules();
    };

    if (loading) return <div className="p-8">Loading modules...</div>;

    return (
        <div className="min-h-screen bg-slate-950 text-white px-8 py-12">
            <h1 className="text-4xl font-bold mb-6">Platform Module Manager</h1>

            <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8 grid md:grid-cols-3 gap-4">
                {["moduleKey", "name", "category", "technology", "version", "status", "pluginType", "assemblyName", "entryPoint", "dependencies"].map((field) => (
                    <input
                        key={field}
                        name={field}
                        value={form[field] || ""}
                        onChange={handleChange}
                        placeholder={field}
                        className="bg-slate-800 border border-slate-700 rounded px-3 py-2"
                    />
                ))}

                <input
                    name="executionOrder"
                    type="number"
                    value={form.executionOrder}
                    onChange={handleChange}
                    placeholder="executionOrder"
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2"
                />

                <textarea
                    name="description"
                    value={form.description || ""}
                    onChange={handleChange}
                    placeholder="description"
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 md:col-span-3"
                />

                <textarea
                    name="configurationJson"
                    value={form.configurationJson || ""}
                    onChange={handleChange}
                    placeholder="configurationJson"
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 md:col-span-3"
                />

                {["isEnabled", "isCoreModule", "supportsHotReload", "isResearchModule", "isExperimental"].map((field) => (
                    <label key={field} className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            name={field}
                            checked={!!form[field]}
                            onChange={handleChange}
                        />
                        {field}
                    </label>
                ))}

                <div className="md:col-span-3 flex gap-3">
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 px-5 py-2 rounded">
                        {editingId ? "Update Module" : "Create Module"}
                    </button>

                    {editingId && (
                        <button
                            type="button"
                            onClick={() => {
                                setEditingId(null);
                                setForm(emptyForm);
                            }}
                            className="bg-slate-700 hover:bg-slate-600 px-5 py-2 rounded"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-xl">
                <table className="w-full text-left">
                    <thead className="bg-slate-800">
                        <tr>
                            <th className="p-4">Name</th>
                            <th className="p-4">Type</th>
                            <th className="p-4">Category</th>
                            <th className="p-4">Version</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Enabled</th>
                            <th className="p-4">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {modules.map((m) => (
                            <tr key={m.id} className="border-t border-slate-800">
                                <td className="p-4 font-semibold">{m.name}</td>
                                <td className="p-4">{m.pluginType}</td>
                                <td className="p-4">{m.category}</td>
                                <td className="p-4">{m.version}</td>
                                <td className="p-4">{m.status}</td>
                                <td className="p-4">{m.isEnabled ? "Yes" : "No"}</td>
                                <td className="p-4 flex gap-2">
                                    {m.isEnabled ? (
                                        <button
                                            onClick={() => handleDisable(m.id)}
                                            className="bg-yellow-600 px-3 py-2 rounded"
                                        >
                                            Disable
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleEnable(m.id)}
                                            className="bg-green-600 px-3 py-2 rounded"
                                        >
                                            Enable
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleEdit(m)}
                                        className="bg-blue-600 px-3 py-2 rounded"
                                    >
                                        Edit
                                    </button>

                                    <button
                                        onClick={() => handleDelete(m.id)}
                                        className="bg-red-600 px-3 py-2 rounded"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PlatformModuleManagerPage;

